import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ahobilam.com';
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const ServerHealthView = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restartingService, setRestartingService] = useState(null);
  const [cleaningDisk, setCleaningDisk] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [pulseKey, setPulseKey] = useState(0);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { 'Authorization': `Bearer ${token}` } };
  };

  // Normalize the API response into a flat shape the UI expects
  const normalizeHealth = (data) => {
    const srv = data.server || {};
    const servicesObj = data.services || {};
    // Convert services object { "backend": {...}, "mongodb": {...} } into an array
    const servicesArr = Object.entries(servicesObj).map(([key, val]) => ({
      name: key.charAt(0).toUpperCase() + key.slice(1),
      status: val.status || 'unknown',
      pid: val.pid || null,
      uptime: val.uptime || null,
      memory: val.memory_mb ? `${val.memory_mb} MB` : null,
    }));
    // Normalize endpoints (response_ms → response_time in seconds)
    const endpoints = (data.endpoints || []).map(ep => ({
      ...ep,
      response_time: ep.response_ms != null ? (ep.response_ms / 1000) : ep.response_time,
    }));
    // Convert uptime_seconds into days/hours/minutes
    const totalSec = srv.uptime_seconds || 0;
    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    return {
      cpu_percent: srv.cpu_percent,
      memory: {
        percent: srv.memory_percent,
        used_mb: srv.memory_used_mb,
        total_mb: srv.memory_total_mb,
      },
      disk: {
        percent: srv.disk_percent,
        used_gb: srv.disk_used_gb,
        total_gb: srv.disk_total_gb,
        free_gb: srv.disk_free_gb,
      },
      uptime: { days, hours, minutes },
      services: servicesArr,
      endpoints,
      disk_alert: data.disk_alert,
    };
  };

  const fetchHealth = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);
      const res = await axios.get(`${API}/health/full`, getAuthHeaders());
      setHealth(normalizeHealth(res.data));
      setLastRefresh(new Date());
      setPulseKey(prev => prev + 1);
    } catch (err) {
      console.error('Error fetching server health:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to fetch server health');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(() => fetchHealth(false), 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRestartService = async (serviceName) => {
    if (!window.confirm(`Are you sure you want to restart "${serviceName}"?`)) return;
    try {
      setRestartingService(serviceName);
      await axios.post(`${API}/health/restart-service`, { service: serviceName }, getAuthHeaders());
      alert(`${serviceName} restarted successfully!`);
      await fetchHealth(false);
    } catch (err) {
      alert('Failed to restart service: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRestartingService(null);
    }
  };

  const handleCleanDisk = async () => {
    if (!window.confirm('Run disk cleanup? This will remove temporary and cached files.')) return;
    try {
      setCleaningDisk(true);
      const res = await axios.post(`${API}/health/cleanup-disk`, {}, getAuthHeaders());
      alert(res.data?.message || 'Disk cleanup completed!');
      await fetchHealth(false);
    } catch (err) {
      alert('Disk cleanup failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCleaningDisk(false);
    }
  };

  const handleFullReport = async () => {
    try {
      setGeneratingReport(true);
      const res = await axios.post(`${API}/health/run-healthcheck`, {}, getAuthHeaders());
      const reportText = typeof res.data === 'string' ? res.data : JSON.stringify(res.data, null, 2);
      const blob = new Blob([reportText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `server-health-report-${new Date().toISOString().slice(0, 10)}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to generate report: ' + (err.response?.data?.detail || err.message));
    } finally {
      setGeneratingReport(false);
    }
  };

  const getStatusColor = (status) => {
    if (status === 'running' || status === 'active') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (status === 'stopped' || status === 'inactive') return 'bg-red-100 text-red-700 border-red-200';
    return 'bg-yellow-100 text-yellow-700 border-yellow-200';
  };

  const getEndpointBadge = (status) => {
    if (status === 200) return { bg: 'bg-emerald-500', text: '200 OK' };
    if (status === 404) return { bg: 'bg-red-500', text: '404' };
    if (status >= 500) return { bg: 'bg-red-600', text: `${status} ERR` };
    return { bg: 'bg-yellow-500', text: `${status}` };
  };

  const getDiskWarningLevel = (percent) => {
    if (percent >= 90) return { gradient: 'from-red-600 to-red-500', icon: '🔴', urgency: 'CRITICAL' };
    if (percent >= 75) return { gradient: 'from-orange-600 to-red-500', icon: '🟠', urgency: 'WARNING' };
    if (percent >= 60) return { gradient: 'from-yellow-500 to-orange-500', icon: '🟡', urgency: 'NOTICE' };
    return null;
  };

  const getBarColor = (percent) => {
    if (percent >= 90) return 'bg-red-500';
    if (percent >= 75) return 'bg-orange-500';
    if (percent >= 50) return 'bg-yellow-500';
    return 'bg-emerald-500';
  };

  // Loading skeleton
  if (loading && !health) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="relative w-20 h-20 mx-auto mb-6">
                <div className="absolute inset-0 rounded-full border-4 border-blue-500/20"></div>
                <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-500 animate-spin"></div>
                <div className="absolute inset-3 rounded-full border-4 border-transparent border-t-cyan-400 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
              </div>
              <p className="text-blue-300 text-lg font-medium animate-pulse">Connecting to server...</p>
              <p className="text-gray-500 text-sm mt-2">Fetching health metrics</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !health) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-96">
            <div className="bg-red-900/30 border border-red-500/30 rounded-2xl p-10 text-center max-w-md backdrop-blur-sm">
              <div className="text-5xl mb-4">⚠️</div>
              <h3 className="text-red-300 text-xl font-bold mb-3">Connection Failed</h3>
              <p className="text-red-400/80 text-sm mb-6">{error}</p>
              <button
                onClick={() => fetchHealth()}
                className="bg-red-600 hover:bg-red-500 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg shadow-red-900/30"
              >
                🔄 Retry Connection
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const diskWarning = health?.disk ? getDiskWarningLevel(health.disk.percent) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 p-4 md:p-6 lg:p-8" style={{ fontFamily: "'Inter', 'Noto Sans Tamil', 'Segoe UI', system-ui, sans-serif" }}>
      <div className="max-w-7xl mx-auto space-y-6">

        {/* ─── HEADER ─── */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight flex items-center gap-3">
              <span className="text-4xl">🖥</span>
              <span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-400 bg-clip-text text-transparent">
                Server Health & Controls
              </span>
            </h1>
            <p className="text-gray-500 text-sm mt-2 flex items-center gap-2">
              {lastRefresh && (
                <>
                  <span key={pulseKey} className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                  </span>
                  Last updated: {lastRefresh.toLocaleTimeString()} · Auto-refresh every 30s
                </>
              )}
            </p>
          </div>
          <button
            onClick={() => fetchHealth(false)}
            disabled={loading}
            className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300 hover:text-white px-4 py-2.5 rounded-xl transition-all duration-200 text-sm font-medium backdrop-blur-sm"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* ─── DISK SPACE WARNING BANNER ─── */}
        {diskWarning && health?.disk && (
          <div className={`bg-gradient-to-r ${diskWarning.gradient} rounded-2xl p-5 shadow-2xl shadow-red-900/20 border border-white/10 animate-pulse-slow`}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm text-3xl shrink-0">
                  💾
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-white/80 bg-white/20 px-2 py-0.5 rounded-full">
                      {diskWarning.urgency}
                    </span>
                    <span className="text-xs font-bold uppercase tracking-wider text-white/80">
                      DISK SPACE
                    </span>
                  </div>
                  <p className="text-white text-lg font-bold">
                    {diskWarning.icon} {health.disk.percent.toFixed(1)}% disk space used
                    <span className="text-white/70 font-normal text-sm ml-2">
                      ({health.disk.used_gb?.toFixed(1)} GB / {health.disk.total_gb?.toFixed(1)} GB — {health.disk.free_gb?.toFixed(1)} GB free)
                    </span>
                  </p>
                </div>
              </div>
              <button
                onClick={handleCleanDisk}
                disabled={cleaningDisk}
                className="bg-white text-red-600 hover:bg-red-50 font-bold px-6 py-3 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg text-sm whitespace-nowrap disabled:opacity-50"
              >
                {cleaningDisk ? '⏳ Cleaning...' : '🧹 Clean Now'}
              </button>
            </div>
            <div className="mt-3 w-full bg-white/20 rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-all duration-1000"
                style={{ width: `${Math.min(health.disk.percent, 100)}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* ─── METRIC CARDS ROW ─── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* CPU Usage */}
          <div className="bg-gradient-to-br from-blue-600/20 to-blue-900/30 border border-blue-500/20 rounded-2xl p-5 backdrop-blur-sm hover:border-blue-400/40 transition-all duration-300 group">
            <div className="flex items-center justify-between mb-3">
              <span className="text-blue-400 text-xs font-bold uppercase tracking-wider">CPU Usage</span>
              <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center text-xl group-hover:scale-110 transition-transform">
                ⚡
              </div>
            </div>
            <div className="text-4xl font-black text-white tracking-tight">
              {health?.cpu_percent?.toFixed(1) ?? '—'}%
            </div>
            <div className="mt-3 w-full bg-blue-900/50 rounded-full h-2.5 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-1000 ease-out ${getBarColor(health?.cpu_percent || 0)}`}
                style={{ width: `${Math.min(health?.cpu_percent || 0, 100)}%` }}
              ></div>
            </div>
          </div>

          {/* Memory */}
          <div className="bg-gradient-to-br from-purple-600/20 to-purple-900/30 border border-purple-500/20 rounded-2xl p-5 backdrop-blur-sm hover:border-purple-400/40 transition-all duration-300 group">
            <div className="flex items-center justify-between mb-3">
              <span className="text-purple-400 text-xs font-bold uppercase tracking-wider">Memory</span>
              <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center text-xl group-hover:scale-110 transition-transform">
                🧠
              </div>
            </div>
            <div className="text-4xl font-black text-white tracking-tight">
              {health?.memory?.percent?.toFixed(1) ?? '—'}%
            </div>
            <p className="text-purple-300/60 text-xs mt-1">
              {health?.memory?.used_mb ? `${(health.memory.used_mb / 1024).toFixed(1)} GB` : '—'} / {health?.memory?.total_mb ? `${(health.memory.total_mb / 1024).toFixed(1)} GB` : '—'}
            </p>
            <div className="mt-2 w-full bg-purple-900/50 rounded-full h-2.5 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-1000 ease-out ${getBarColor(health?.memory?.percent || 0)}`}
                style={{ width: `${Math.min(health?.memory?.percent || 0, 100)}%` }}
              ></div>
            </div>
          </div>

          {/* Disk Space */}
          <div className="bg-gradient-to-br from-amber-600/20 to-amber-900/30 border border-amber-500/20 rounded-2xl p-5 backdrop-blur-sm hover:border-amber-400/40 transition-all duration-300 group">
            <div className="flex items-center justify-between mb-3">
              <span className="text-amber-400 text-xs font-bold uppercase tracking-wider">Disk Space</span>
              <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center text-xl group-hover:scale-110 transition-transform">
                💾
              </div>
            </div>
            <div className="text-4xl font-black text-white tracking-tight">
              {health?.disk?.percent?.toFixed(1) ?? '—'}%
            </div>
            <p className="text-amber-300/60 text-xs mt-1">
              {health?.disk?.used_gb?.toFixed(1) ?? '—'} GB / {health?.disk?.total_gb?.toFixed(1) ?? '—'} GB
            </p>
            <div className="mt-2 w-full bg-amber-900/50 rounded-full h-2.5 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-1000 ease-out ${getBarColor(health?.disk?.percent || 0)}`}
                style={{ width: `${Math.min(health?.disk?.percent || 0, 100)}%` }}
              ></div>
            </div>
          </div>

          {/* Server Uptime */}
          <div className="bg-gradient-to-br from-emerald-600/20 to-emerald-900/30 border border-emerald-500/20 rounded-2xl p-5 backdrop-blur-sm hover:border-emerald-400/40 transition-all duration-300 group">
            <div className="flex items-center justify-between mb-3">
              <span className="text-emerald-400 text-xs font-bold uppercase tracking-wider">Server Uptime</span>
              <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center text-xl group-hover:scale-110 transition-transform">
                ⏱️
              </div>
            </div>
            <div className="text-3xl font-black text-white tracking-tight">
              {health?.uptime?.days ?? '—'}
              <span className="text-lg text-emerald-300/70 font-medium ml-1">days</span>
            </div>
            <p className="text-emerald-300/60 text-xs mt-1">
              {health?.uptime ? `${health.uptime.hours}h ${health.uptime.minutes}m` : '—'}
            </p>
            {health?.uptime?.process && (
              <p className="text-emerald-400/40 text-xs mt-1 truncate" title={health.uptime.process}>
                PID: {health.uptime.process}
              </p>
            )}
          </div>
        </div>

        {/* ─── SERVICES TABLE ─── */}
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden backdrop-blur-sm">
          <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan-500/20 rounded-xl flex items-center justify-center text-xl">⚙️</div>
              <div>
                <h2 className="text-white text-lg font-bold tracking-tight">Services</h2>
                <p className="text-gray-500 text-xs mt-0.5">
                  {health?.services?.filter(s => s.status === 'running').length || 0} / {health?.services?.length || 0} running
                </p>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left py-3.5 px-6 text-xs font-bold uppercase tracking-wider text-gray-500">Service</th>
                  <th className="text-left py-3.5 px-6 text-xs font-bold uppercase tracking-wider text-gray-500">Status</th>
                  <th className="text-left py-3.5 px-6 text-xs font-bold uppercase tracking-wider text-gray-500">PID</th>
                  <th className="text-left py-3.5 px-6 text-xs font-bold uppercase tracking-wider text-gray-500">Uptime</th>
                  <th className="text-left py-3.5 px-6 text-xs font-bold uppercase tracking-wider text-gray-500">Memory</th>
                  <th className="text-right py-3.5 px-6 text-xs font-bold uppercase tracking-wider text-gray-500">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {(health?.services || []).map((service, idx) => (
                  <tr key={service.name || idx} className="hover:bg-white/5 transition-colors duration-150">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-lg flex items-center justify-center">
                          <span className="text-sm">
                            {service.name?.toLowerCase().includes('mongo') ? '🍃' :
                             service.name?.toLowerCase().includes('mysql') ? '🐬' :
                             service.name?.toLowerCase().includes('apache') ? '🪶' :
                             service.name?.toLowerCase().includes('backend') ? '🚀' : '📦'}
                          </span>
                        </div>
                        <span className="text-white font-semibold text-sm">{service.name}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${getStatusColor(service.status)}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${service.status === 'running' ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                        {service.status}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-400 text-sm font-mono">{service.pid || '—'}</td>
                    <td className="py-4 px-6 text-gray-400 text-sm">{service.uptime || '—'}</td>
                    <td className="py-4 px-6 text-gray-400 text-sm">{service.memory || '—'}</td>
                    <td className="py-4 px-6 text-right">
                      <button
                        onClick={() => handleRestartService(service.name)}
                        disabled={restartingService === service.name}
                        className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 hover:text-orange-300 border border-orange-500/30 px-4 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {restartingService === service.name ? (
                          <span className="flex items-center gap-1.5">
                            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                            Restarting...
                          </span>
                        ) : (
                          '🔄 Restart'
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
                {(!health?.services || health.services.length === 0) && (
                  <tr>
                    <td colSpan={6} className="py-10 text-center text-gray-500 text-sm">No services data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ─── ENDPOINT HEALTH ─── */}
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden backdrop-blur-sm">
          <div className="px-6 py-5 border-b border-white/10 flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-500/20 rounded-xl flex items-center justify-center text-xl">🌐</div>
            <div>
              <h2 className="text-white text-lg font-bold tracking-tight">Endpoint Health</h2>
              <p className="text-gray-500 text-xs mt-0.5">Live status of all monitored endpoints</p>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(health?.endpoints || []).map((ep, idx) => {
                const badge = getEndpointBadge(ep.status);
                return (
                  <div
                    key={ep.name || idx}
                    className="bg-white/5 border border-white/10 rounded-xl p-5 hover:border-white/20 transition-all duration-300 group hover:bg-white/[0.07]"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-white font-bold text-sm">{ep.name}</h3>
                      <span className={`${badge.bg} text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm`}>
                        {badge.text}
                      </span>
                    </div>
                    <p className="text-gray-500 text-xs truncate mb-3 font-mono" title={ep.url}>{ep.url}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-xs">Response Time</span>
                      <span className={`font-bold text-sm ${
                        ep.response_time && parseFloat(ep.response_time) < 1 ? 'text-emerald-400' :
                        ep.response_time && parseFloat(ep.response_time) < 3 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {ep.response_time ? (typeof ep.response_time === 'number' ? `${ep.response_time.toFixed(2)}s` : ep.response_time) : '—'}
                      </span>
                    </div>
                    <div className="mt-3 w-full bg-gray-800 rounded-full h-1.5 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${
                          ep.status === 200 ? 'bg-emerald-500' : 'bg-red-500'
                        }`}
                        style={{ width: ep.status === 200 ? '100%' : '30%' }}
                      ></div>
                    </div>
                  </div>
                );
              })}
              {(!health?.endpoints || health.endpoints.length === 0) && (
                <div className="col-span-full text-center py-8 text-gray-500 text-sm">No endpoint data available</div>
              )}
            </div>
          </div>
        </div>

        {/* ─── BOTTOM ACTION BUTTONS ─── */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 pt-2 pb-4">
          <button
            onClick={() => fetchHealth()}
            disabled={loading}
            className="flex items-center justify-center gap-2.5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-bold px-8 py-3.5 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg shadow-blue-900/30 disabled:opacity-50 disabled:transform-none text-sm"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh Health
          </button>

          <button
            onClick={handleCleanDisk}
            disabled={cleaningDisk}
            className="flex items-center justify-center gap-2.5 bg-gradient-to-r from-emerald-600 to-green-500 hover:from-emerald-500 hover:to-green-400 text-white font-bold px-8 py-3.5 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg shadow-emerald-900/30 disabled:opacity-50 disabled:transform-none text-sm"
          >
            {cleaningDisk ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                Cleaning...
              </>
            ) : (
              <>🧹 Clean Disk Space</>
            )}
          </button>

          <button
            onClick={handleFullReport}
            disabled={generatingReport}
            className="flex items-center justify-center gap-2.5 bg-gradient-to-r from-red-600 to-rose-500 hover:from-red-500 hover:to-rose-400 text-white font-bold px-8 py-3.5 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg shadow-red-900/30 disabled:opacity-50 disabled:transform-none text-sm"
          >
            {generatingReport ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                Generating...
              </>
            ) : (
              <>📋 Full Health Report</>
            )}
          </button>
        </div>

        {/* ─── FOOTER ─── */}
        <div className="text-center text-gray-600 text-xs pb-4">
          Server Health Monitor · Ahobilam.com · {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
};

export default ServerHealthView;
